import { EmailHistory } from '../email/EmailHistory';
import AccountBalanceWalletRounded from '@mui/icons-material/AccountBalanceWalletRounded';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import CreditScoreRounded from '@mui/icons-material/CreditScoreRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import EmailOutlined from '@mui/icons-material/EmailOutlined';
import EventRounded from '@mui/icons-material/EventRounded';
import PaymentsRounded from '@mui/icons-material/PaymentsRounded';
import PersonOffRounded from '@mui/icons-material/PersonOffRounded';
import PhoneOutlined from '@mui/icons-material/PhoneOutlined';
import PlaceOutlined from '@mui/icons-material/PlaceOutlined';
import PrintRounded from '@mui/icons-material/PrintRounded';
import ShoppingBagRounded from '@mui/icons-material/ShoppingBagRounded';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { ApiError, api, unwrap } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ImageEditor } from '../../components/images';
import { useImageSrc } from '../../lib/images';
import { EmptyState } from '../../components/states';
import { initials, money } from '../../lib/format';
import { useNotify } from '../../lib/notify';
import { useShop } from '../../shop/ShopProvider';
import { ContactFormDialog } from './ContactFormDialog';
import { LedgerTab } from './LedgerTab';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import MailOutlineRounded from '@mui/icons-material/MailOutlineRounded';
import { useEmail } from '../email/api';
import { useWhatsapp, WHATSAPP_GREEN, WhatsappHistory } from '../whatsapp';
import { AttachmentsPanel } from '../attachments';
import {
  balanceTone,
  contactKeys,
  creditUsage,
  KIND,
  presetRange,
  todayIn,
  trimDecimal,
  type Contact,
  type ContactKind,
  type Ledger,
} from './model';
import { OpeningBalancesTab } from './OpeningBalancesTab';
import { OpenItemsTab } from './OpenItemsTab';
import { DateRangeBar, Field, RoleChips, StatCard } from './ui';
import { MoreMenu } from '../../components/MoreMenu';
import { useLinked } from '../../components/useLinked';
import { useLinkedPayments } from '../../components/useLinkedPayments';

type TabKey = 'ledger' | 'open' | 'opening' | 'details';

/** /customers/:id and /suppliers/:id — header, balance cards, ledger and related tabs. */
export function ContactDetailPage({ kind }: { kind: ContactKind }) {
  const { id = '' } = useParams();
  const k = KIND[kind];
  const navigate = useNavigate();
  const { can } = useAuth();
  const { format } = useShop();
  const [params, setParams] = useSearchParams();
  const today = todayIn(format.timeZone);
  const defaults = presetRange('thisYear', today);
  const from = params.get('from') ?? defaults.from;
  const to = params.get('to') ?? defaults.to;
  const [editing, setEditing] = useState(false);

  const data = useQuery({
    queryKey: contactKeys.detail(id),
    queryFn: () => unwrap(api.GET('/contacts/{id}', { params: { path: { id } } })),
    enabled: !!id,
  });
  const c = data.data;

  const set = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [key, v] of Object.entries(next)) {
      if (v === null) p.delete(key);
      else p.set(key, v);
    }
    // Switching tabs is a step Back returns from; date and ledger changes are not.
    setParams(p, { replace: !('tab' in next) });
  };

  if (data.error) {
    const missing = data.error instanceof ApiError && data.error.status === 404;
    return (
      <Card>
        <EmptyState
          icon={<PersonOffRounded />}
          title={missing ? `This ${k.singular} doesn’t exist` : `Couldn’t load this ${k.singular}`}
          body={missing ? 'It may have been deleted, or the link is wrong.' : data.error.message}
          action={{
            label: `Back to ${k.title.toLowerCase()}`,
            onClick: () => void navigate(k.base),
          }}
        />
      </Card>
    );
  }

  const ledgers: Ledger[] = c
    ? [
        ...(c.isCustomer || Number(c.balances.receivable.amount) ? (['RECEIVABLE'] as const) : []),
        ...(c.isSupplier || Number(c.balances.payable.amount) ? (['PAYABLE'] as const) : []),
      ]
    : [k.ledger];
  const wanted = (params.get('ledger') as Ledger | null) ?? k.ledger;
  const ledger: Ledger = ledgers.includes(wanted) ? wanted : (ledgers[0] ?? k.ledger);

  const tabs: { key: TabKey; label: string; show: boolean }[] = [
    { key: 'ledger', label: 'Ledger', show: can('ledger.view') },
    { key: 'open', label: 'Open documents', show: can('payments.view') },
    { key: 'opening', label: 'Opening balances', show: true },
    { key: 'details', label: 'Details', show: true },
  ];
  const visible = tabs.filter((t) => t.show);
  const tabParam = params.get('tab') as TabKey | null;
  const tab: TabKey = visible.some((t) => t.key === tabParam)
    ? (tabParam as TabKey)
    : (visible[0]?.key ?? 'details');

  return (
    <>
      <Button
        size="small"
        color="inherit"
        startIcon={<ArrowBackRounded />}
        onClick={() => void navigate(k.base)}
        sx={{ mb: 1.5, ml: -1, color: 'text.secondary' }}
      >
        {k.title}
      </Button>

      <Header
        contact={c}
        kind={kind}
        onEdit={() => setEditing(true)}
        ledger={ledger}
        from={from}
        to={to}
      />

      <Box
        sx={{
          display: 'grid',
          gap: 2.5,
          gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
          my: 3,
        }}
      >
        <SummaryCards contact={c} />
      </Box>

      <Card sx={{ mb: 2.5 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          sx={{
            alignItems: { md: 'center' },
            justifyContent: 'space-between',
            px: { xs: 1, sm: 2 },
          }}
        >
          <Tabs
            value={tab}
            onChange={(_, v: TabKey) => set({ tab: v === 'ledger' ? null : v })}
            variant="scrollable"
            allowScrollButtonsMobile
          >
            {visible.map((t) => (
              <Tab key={t.key} value={t.key} label={t.label} />
            ))}
          </Tabs>
          {ledgers.length > 1 && (tab === 'ledger' || tab === 'open') && (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={ledger}
              onChange={(_, v: Ledger | null) => v && set({ ledger: v })}
              sx={{ py: 1, px: { xs: 1, md: 0 } }}
              aria-label="Ledger"
            >
              <ToggleButton value="RECEIVABLE">Receivable</ToggleButton>
              <ToggleButton value="PAYABLE">Payable</ToggleButton>
            </ToggleButtonGroup>
          )}
        </Stack>
      </Card>

      {!c ? (
        <Card>
          <CardContent>
            <Skeleton height={36} width="40%" />
            <Skeleton height={28} />
            <Skeleton height={28} />
            <Skeleton height={28} width="80%" />
          </CardContent>
        </Card>
      ) : tab === 'ledger' ? (
        <Stack spacing={2}>
          <DateRangeBar
            from={from}
            to={to}
            today={today}
            onChange={(r) => set({ from: r.from, to: r.to })}
          />
          <LedgerTab contact={c} ledger={ledger} from={from} to={to} />
        </Stack>
      ) : tab === 'open' ? (
        <OpenItemsTab contact={c} ledger={ledger} />
      ) : tab === 'opening' ? (
        <OpeningBalancesTab contact={c} kind={kind} />
      ) : (
        <DetailsPanel contact={c} />
      )}
      {c && <AttachmentsPanel recordType="CONTACT" recordId={c.id} sx={{ mt: 3 }} />}
      {c && <WhatsappHistory contactId={c.id} sx={{ mt: 3 }} />}
      {c && <EmailHistory contactId={c.id} sx={{ mt: 3 }} />}

      {c && (
        <ContactFormDialog
          open={editing}
          kind={kind}
          contact={c}
          onClose={() => setEditing(false)}
        />
      )}
    </>
  );
}

function Header({
  contact: c,
  kind,
  ledger,
  from,
  to,
  onEdit,
}: {
  contact: Contact | undefined;
  kind: ContactKind;
  ledger: Ledger;
  from: string;
  to: string;
  onEdit: () => void;
}) {
  const k = KIND[kind];
  const wa = useWhatsapp();
  const email = useEmail();
  const { can } = useAuth();
  const navigate = useNavigate();
  const notify = useNotify();
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState<'delete' | 'deactivate' | null>(null);
  const customer = c && kind === 'customer' && !c.isWalkIn ? { contactId: c.id } : null;
  const supplier = c && kind === 'supplier' ? { contactId: c.id } : null;
  const linked = [
    useLinked('quotations', customer),
    useLinked('orders', customer),
    useLinked('invoices', customer),
    useLinked('purchaseOrders', supplier),
    useLinked('bills', supplier),
    useLinkedPayments(customer ?? supplier),
  ];

  const toggleActive = useMutation({
    mutationFn: (contact: Contact) =>
      unwrap(
        api.PATCH('/contacts/{id}', {
          params: { path: { id: contact.id } },
          body: { isActive: !contact.isActive, version: contact.version },
        }),
      ),
    onSuccess: async (saved) => {
      notify.success(saved.isActive ? `${saved.name} reactivated` : `${saved.name} deactivated`);
      await qc.invalidateQueries({ queryKey: contactKeys.all });
    },
    onError: (e) => notify.error(e),
  });

  if (!c)
    return (
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <Skeleton variant="rounded" width={64} height={64} sx={{ borderRadius: '18px' }} />
        <Box sx={{ flex: 1 }}>
          <Skeleton width={260} height={38} />
          <Skeleton width={180} />
        </Box>
      </Stack>
    );

  const canUpdate = can(`${k.perm}.update`);
  const canDelete = can(`${k.perm}.delete`);
  // A contact that is both customer and supplier needs both update permissions (as on the API).
  const canEditPhoto =
    (!c.isCustomer || can('customers.update')) && (!c.isSupplier || can('suppliers.update'));
  const info: { icon: ReactNode; text: string }[] = [
    ...(c.phone ? [{ icon: <PhoneOutlined />, text: c.phone }] : []),
    ...(c.email ? [{ icon: <EmailOutlined />, text: c.email }] : []),
    ...(c.address ? [{ icon: <PlaceOutlined />, text: c.address.split('\n')[0] ?? '' }] : []),
  ];

  return (
    <>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2.5}
        sx={{ alignItems: { md: 'center' }, justifyContent: 'space-between' }}
      >
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center', minWidth: 0 }}>
          <ImageEditor
            target={{ kind: 'contact', id: c.id }}
            url={c.imageUrl}
            canEdit={canEditPhoto}
            label={c.name}
          >
            <ContactPhoto name={c.name} imageUrl={c.imageUrl} />
          </ImageEditor>
          <Box sx={{ minWidth: 0 }}>
            <Stack
              direction="row"
              spacing={1.25}
              sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.75 }}
            >
              <Typography variant="h3" sx={{ wordBreak: 'break-word' }}>
                {c.name}
              </Typography>
              <RoleChips
                isCustomer={c.isCustomer}
                isSupplier={c.isSupplier}
                isActive={c.isActive}
                isWalkIn={c.isWalkIn}
              />
            </Stack>
            <Stack
              direction="row"
              spacing={2}
              sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 0.5, color: 'text.secondary' }}
            >
              {info.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No phone or email on file
                </Typography>
              )}
              {info.map((i) => (
                <Stack
                  key={i.text}
                  direction="row"
                  spacing={0.5}
                  sx={{ alignItems: 'center', '& svg': { fontSize: 16 } }}
                >
                  {i.icon}
                  <Typography variant="body2">{i.text}</Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        </Stack>
        <Stack
          direction="row"
          spacing={1.25}
          sx={{ flexShrink: 0, flexWrap: 'wrap', rowGap: 1.25 }}
        >
          {can('payments.create') && !c.isWalkIn && (
            <Button
              variant="outlined"
              startIcon={<PaymentsRounded />}
              onClick={() => {
                const purpose = ledger === 'PAYABLE' ? 'SUPPLIER_PAYMENT' : 'RECEIPT';
                void navigate(`/payments/new?purpose=${purpose}&contactId=${c.id}`);
              }}
            >
              {ledger === 'PAYABLE' ? 'Pay supplier' : 'Receive payment'}
            </Button>
          )}
          {can('ledger.view') && (
            <Button
              variant="outlined"
              startIcon={<PrintRounded />}
              onClick={() => {
                const q = new URLSearchParams({ ledger, to });
                if (from) q.set('from', from);
                void navigate(`${k.base}/${c.id}/statement?${q.toString()}`);
              }}
            >
              Statement
            </Button>
          )}
          {can('ledger.view') && wa.canSend && wa.isOn('contact_ledger') && !c.isWalkIn && (
            <Button
              variant="contained"
              startIcon={<WhatsAppIcon />}
              onClick={() => {
                const q = new URLSearchParams({ ledger, to, whatsapp: '1' });
                if (from) q.set('from', from);
                void navigate(`${k.base}/${c.id}/statement?${q.toString()}`);
              }}
              sx={{ bgcolor: WHATSAPP_GREEN, color: '#fff', '&:hover': { bgcolor: '#178A43' } }}
            >
              Send ledger
            </Button>
          )}
          {can('ledger.view') &&
            email.canSend &&
            !!email.sender('contact_ledger') &&
            !c.isWalkIn && (
              <Button
                variant="outlined"
                startIcon={<MailOutlineRounded />}
                onClick={() => {
                  const q = new URLSearchParams({ ledger, to, email: '1' });
                  if (from) q.set('from', from);
                  void navigate(`${k.base}/${c.id}/statement?${q.toString()}`);
                }}
              >
                Email ledger
              </Button>
            )}
          {canUpdate && (
            <Button variant="contained" startIcon={<EditRounded />} onClick={onEdit}>
              Edit
            </Button>
          )}
          <MoreMenu
            linked={linked}
            exportModel={c.isWalkIn ? undefined : 'contact'}
            exportId={c.id}
            actions={[
              ...(canUpdate && !c.isWalkIn
                ? [
                    {
                      key: 'active',
                      label: c.isActive ? 'Deactivate' : 'Reactivate',
                      icon: <PersonOffRounded fontSize="small" />,
                      onClick: () => {
                        if (c.isActive) setConfirm('deactivate');
                        else toggleActive.mutate(c);
                      },
                    },
                  ]
                : []),
              ...(canDelete && !c.isWalkIn
                ? [
                    {
                      key: 'delete',
                      label: 'Delete',
                      icon: <DeleteOutlineRounded fontSize="small" />,
                      onClick: () => setConfirm('delete'),
                      danger: true,
                    },
                  ]
                : []),
            ]}
          />
        </Stack>
      </Stack>
      <ConfirmDialog
        open={confirm === 'deactivate'}
        title={`Deactivate ${c.name}?`}
        body={`They will be hidden from pickers on new documents. Their history and balance stay as they are, and you can reactivate them at any time.`}
        confirmLabel="Deactivate"
        onConfirm={() => toggleActive.mutateAsync(c).catch(() => undefined)}
        onClose={() => setConfirm(null)}
      />
      <ConfirmDialog
        open={confirm === 'delete'}
        danger
        title={`Delete ${c.name}?`}
        body="Only contacts with nothing posted can be deleted. If they have invoices, bills, payments or an opening balance, deactivate them instead."
        confirmLabel="Delete"
        onConfirm={() =>
          unwrap(
            api.DELETE('/contacts/{id}', {
              params: { path: { id: c.id }, query: { version: c.version } },
            }),
          )
            .then(async () => {
              notify.success(`${c.name} deleted`);
              await qc.invalidateQueries({ queryKey: contactKeys.all });
              void navigate(k.base);
            })
            .catch((e: unknown) => notify.error(e))
        }
        onClose={() => setConfirm(null)}
      />
    </>
  );
}

function SummaryCards({ contact: c }: { contact: Contact | undefined }) {
  const { format } = useShop();
  const loading = !c;
  const usage = c ? creditUsage(c) : null;
  const rec = c?.balances.receivable;
  const pay = c?.balances.payable;
  return (
    <>
      <StatCard
        label="Receivable"
        icon={<AccountBalanceWalletRounded />}
        loading={loading}
        tone={rec ? balanceTone(rec) : 'default'}
        value={rec && money(rec.direction === 'SETTLED' ? 0 : rec.amount, format)}
        hint={rec?.label ?? ''}
      />
      <StatCard
        label="Payable"
        icon={<ShoppingBagRounded />}
        loading={loading}
        tone={pay ? balanceTone(pay) : 'default'}
        value={pay && money(pay.direction === 'SETTLED' ? 0 : pay.amount, format)}
        hint={pay?.label ?? ''}
      />
      <StatCard
        label="Available credit"
        icon={<CreditScoreRounded />}
        loading={loading}
        tone={
          usage ? (usage.over ? 'error' : usage.percent >= 80 ? 'warning' : 'success') : 'default'
        }
        value={usage ? money(Math.max(0, usage.available), format) : 'No limit'}
        hint={
          usage
            ? usage.over
              ? `Over the ${money(usage.limit, format)} limit by ${money(-usage.available, format)}`
              : `${Math.round(usage.percent)}% of ${money(usage.limit, format)} used`
            : c?.isCustomer
              ? 'Credit sales are not capped'
              : 'Only applies to customers'
        }
      >
        {usage && (
          <LinearProgress
            variant="determinate"
            value={usage.percent}
            color={usage.over ? 'error' : usage.percent >= 80 ? 'warning' : 'success'}
            sx={{ mt: 1.25 }}
          />
        )}
      </StatCard>
      <StatCard
        label="Payment terms"
        icon={<EventRounded />}
        loading={loading}
        value={
          c?.paymentTermsDays === null || c?.paymentTermsDays === undefined
            ? 'Not set'
            : c.paymentTermsDays === 0
              ? 'On receipt'
              : `${String(c.paymentTermsDays)} days`
        }
        hint={
          c?.paymentTermsDays
            ? 'Due date of new invoices and bills'
            : 'Documents are due on their date'
        }
      />
    </>
  );
}

function DetailsPanel({ contact: c }: { contact: Contact }) {
  const { format } = useShop();
  return (
    <Card>
      <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
        <Box
          sx={{
            display: 'grid',
            gap: 3,
            gridTemplateColumns: {
              xs: 'minmax(0,1fr)',
              sm: 'repeat(2, 1fr)',
              lg: 'repeat(3, 1fr)',
            },
          }}
        >
          <Field label="Phone">{c.phone ?? '—'}</Field>
          <Field label="Email">{c.email ?? '—'}</Field>
          <Field label="Tax number">{c.taxNumber ?? '—'}</Field>
          <Field label="Address">
            <Box component="span" sx={{ whiteSpace: 'pre-line' }}>
              {c.address ?? '—'}
            </Box>
          </Field>
          <Field label="Credit limit">
            {c.creditLimit === null ? 'No limit' : money(trimDecimal(c.creditLimit), format)}
          </Field>
          <Field label="Payment terms">
            {c.paymentTermsDays === null ? 'Not set' : `${String(c.paymentTermsDays)} days`}
          </Field>
        </Box>
        <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
          <Field label="Notes">
            <Box component="span" sx={{ whiteSpace: 'pre-line', fontWeight: 400 }}>
              {c.notes ?? 'No notes yet.'}
            </Box>
          </Field>
        </Box>
      </CardContent>
    </Card>
  );
}

function ContactPhoto({ name, imageUrl }: { name: string; imageUrl: string | null }) {
  const { src } = useImageSrc(imageUrl);
  return (
    <Avatar
      variant="rounded"
      src={src ?? undefined}
      alt={src ? name : undefined}
      sx={(t) => {
        const p = (t.vars ?? t).palette.primary.main;
        return {
          width: 64,
          height: 64,
          borderRadius: '18px',
          fontSize: '1.375rem',
          fontFamily: t.typography.h1.fontFamily,
          color: '#fff',
          background: `linear-gradient(135deg, ${p}, color-mix(in srgb, ${p} 55%, #000))`,
          boxShadow: `0 8px 20px -8px color-mix(in srgb, ${p} 60%, transparent)`,
        };
      }}
    >
      {initials(name)}
    </Avatar>
  );
}
